import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Users, 
  ShoppingCart, 
  Target, 
  DollarSign,
  Plus,
  Settings,
  ChevronRight,
  TrendingUp,
  Activity,
  Globe,
  Shield,
  CreditCard,
  UserPlus,
  Plug
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SuperAdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect if not super admin
    if (user && !user.is_super_admin) {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
      return;
    }

    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/super-admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-wt-text-secondary">Failed to load dashboard</p>
      </div>
    );
  }

  const { platform_stats, companies } = dashboardData;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6 p-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-6 w-6 text-wt-gold" />
            <h1 className="text-2xl font-bold text-wt-text-primary">Super Admin Dashboard</h1>
          </div>
          <p className="text-wt-text-secondary">Platform-wide overview and management</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate('/super-admin/companies/new')}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Company
          </Button>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Companies</p>
                <p className="text-xl font-bold text-wt-text-primary">{platform_stats.total_companies}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Users className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Total Users</p>
                <p className="text-xl font-bold text-wt-text-primary">{platform_stats.total_users}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Total Orders</p>
                <p className="text-xl font-bold text-wt-text-primary">{platform_stats.total_orders}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Target className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Total Leads</p>
                <p className="text-xl font-bold text-wt-text-primary">{platform_stats.total_leads}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/10 rounded-lg">
                <Users className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Customers</p>
                <p className="text-xl font-bold text-wt-text-primary">{platform_stats.total_customers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-wt-gold/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-wt-gold" />
              </div>
              <div>
                <p className="text-sm text-wt-text-secondary">Revenue</p>
                <p className="text-lg font-bold text-wt-gold">{formatCurrency(platform_stats.total_revenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/companies')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Manage Companies</p>
              <p className="text-sm text-wt-text-secondary">View & edit companies</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/billing')}
          data-testid="billing-management-link"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <CreditCard className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Billing Management</p>
              <p className="text-sm text-wt-text-secondary">Plans & usage tracking</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/users')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <UserPlus className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">User Management</p>
              <p className="text-sm text-wt-text-secondary">Platform users</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/integrations')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-amber-500/10 rounded-lg">
              <Plug className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Integrations</p>
              <p className="text-sm text-wt-text-secondary">API & webhooks</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/platform-credentials')}
          data-testid="platform-credentials-card"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-emerald-500/10 rounded-lg">
              <Plug className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Platform Credentials</p>
              <p className="text-sm text-wt-text-secondary">Amazon OAuth, etc.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Companies List */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <Globe className="h-5 w-5 text-wt-gold" />
                Companies
              </CardTitle>
              <CardDescription className="text-wt-text-secondary">
                Manage all registered companies on the platform
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/super-admin/companies')}
              className="border-wt-border text-wt-text-primary hover:bg-wt-surface-hover"
            >
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {companies.map((company) => (
              <div 
                key={company.id}
                className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/super-admin/companies/${company.id}`)}
                data-testid={`company-card-${company.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-wt-gold/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-wt-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-wt-text-primary">{company.name}</h3>
                    <p className="text-sm text-wt-text-secondary">{company.id}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-wt-text-secondary">Users</p>
                      <p className="font-medium text-wt-text-primary">{company.stats?.users || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-wt-text-secondary">Orders</p>
                      <p className="font-medium text-wt-text-primary">{company.stats?.orders || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-wt-text-secondary">Leads</p>
                      <p className="font-medium text-wt-text-primary">{company.stats?.leads || 0}</p>
                    </div>
                  </div>

                  <Badge 
                    variant={company.status === 'active' ? 'default' : 'secondary'}
                    className={company.status === 'active' ? 'bg-green-500/10 text-green-400 border-green-500/20' : ''}
                  >
                    {company.status}
                  </Badge>

                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/super-admin/companies/${company.id}/settings`);
                    }}
                    className="text-wt-text-secondary hover:text-wt-text-primary"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {companies.length === 0 && (
              <div className="text-center py-8 text-wt-text-secondary">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No companies registered yet</p>
                <Button 
                  className="mt-4 bg-wt-gold hover:bg-wt-gold-dark text-black"
                  onClick={() => navigate('/super-admin/companies/new')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Company
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/companies')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-wt-text-primary">Manage Companies</h3>
              <p className="text-sm text-wt-text-secondary">Create, edit, and configure companies</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/users')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <Users className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h3 className="font-medium text-wt-text-primary">Manage Users</h3>
              <p className="text-sm text-wt-text-secondary">View and manage all platform users</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors cursor-pointer"
          onClick={() => navigate('/super-admin/integrations')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <Activity className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h3 className="font-medium text-wt-text-primary">Integration Status</h3>
              <p className="text-sm text-wt-text-secondary">Monitor all company integrations</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboardPage;
